"use client";

import {
  TEXT_DIM,
  TEXT_MUTE,
} from "@/components/ui/organisms/chord-practice/palette";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { Collapsible } from "../collapsible";
import { ARRIVE, EASE_MOVE, EASE_OUT, FADE, SHIFT, ease, pick } from "../motion";

interface PieceHeroProps {
  title: string;
  composer: string | null;
  /**
   * Already-formatted context line shown below the composer (e.g.
   * `"BO de Clair Obscur: Expedition 33 (2025)"`). Hidden when null ,
   * we do *not* render a ", " placeholder for context, unlike composer
   * (the composer slot is always present for layout stability ; the
   * context slot is opt-in).
   */
  context: string | null;
  /**
   * La tonalité en toutes lettres, ou `null` quand elle n'est pas connue.
   *
   * Elle ne s'affiche qu'à l'état de choix, à côté du compositeur. Les quatre
   * respirations, où elle vit d'ordinaire, s'en vont alors : elle est la seule
   * des quatre à survivre, parce qu'elle est la seule qui dise encore quelque
   * chose de ce qu'on va apprendre.
   */
  tonality: string | null;
  /**
   * La titraille se resserre : c'est l'état de choix.
   *
   * Le titre perd deux cinquièmes de son corps et la ligne de contexte s'en va.
   * Un titre plus petit paraît plus haut, ce qui est exactement ce qu'on
   * cherche : la place descend vers ce qui devient le sujet.
   */
  compact: boolean;
}

/**
 * Une taille de titre, en nombres plutôt qu'en `clamp` déjà écrit.
 *
 * Parce que l'état de choix la réduit d'un facteur : trois bornes à multiplier
 * se multiplient, une chaîne CSS ne se multiplie pas.
 */
type TitleScale = {
  /** Le plancher, le pas au viewport et le plafond du `clamp`. */
  readonly min: number;
  readonly vw: number;
  readonly max: number;
  /** L'interlettrage, en em, toujours négatif sur cette titraille. */
  readonly tracking: number;
  readonly lineHeight: number;
};

/**
 * De combien le titre se réduit quand la fiche se retourne sur ses parcours.
 *
 * Deux cinquièmes en moins. Ce n'est pas seulement une place qu'on libère :
 * c'est le titre qui cesse d'être le sujet de l'écran, et il resserre au
 * passage la titraille contre son surtitre.
 */
const COMPACT_RATIO = 0.58;

/**
 * Et de combien l'interlettrage se relâche.
 *
 * Un interlettrage négatif se règle pour un corps donné : gardé tel quel sur un
 * corps réduit de moitié, les lettres se touchent.
 */
const COMPACT_TRACKING = 0.67;

/**
 * Centerpiece of the overlay : oversized piece title with a soft
 * top-to-bottom gradient, composer below in dimmed grey. Other
 * "fiche d'identité" data has migrated to the eyebrow row, so the
 * hero stays maximally calm and visual.
 *
 * Two adaptive behaviors :
 *
 *  - **Size scaling** : the font-size shrinks by tiers as the title
 *    grows, so a long file name (e.g. "liberee delivree la reine des
 *    neiges") still fits without crushing the layout. Each tier still
 *    uses `clamp` against the viewport for responsive headroom.
 *
 *  - **Sentence-case prettifying** : when the source title is all
 *    lowercase or all uppercase (typical of MIDI metadata that wasn't
 *    cared for), we capitalize the first letter and lowercase the
 *    rest. Mixed-case titles are passed through untouched, we trust
 *    the user's choice.
 *
 * La titraille a un second état, celui du choix des parcours : elle se réduit
 * et remonte, sans quitter l'axe. Purement vertical, donc une seule propriété
 * animée, et le titre reste le même objet au lieu d'être remplacé.
 */
export function PieceHero({
  title,
  composer,
  context,
  tonality,
  compact,
}: PieceHeroProps) {
  const reduced = useReducedMotion();
  const displayTitle = prettifyTitle(title);
  const scale = pickScale(displayTitle);
  const shift = pick(SHIFT, compact);
  const ratio = compact ? COMPACT_RATIO : 1;
  const tracking = compact ? scale.tracking * COMPACT_TRACKING : scale.tracking;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "0 40px",
      }}
    >
      <h1
        style={{
          fontSize: clampPx(scale, ratio),
          fontFamily: "Inter, sans-serif",
          fontWeight: 200,
          lineHeight: scale.lineHeight,
          letterSpacing: `${tracking}em`,
          // Reserves room under the baseline for descenders (g, y, p, q,
          // j). Tight line-height + `background-clip: text` together
          // crop them on most browsers without this padding.
          paddingBottom: "0.15em",
          margin: 0,
          maxWidth: 1100,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.45) 95%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          color: "transparent",
          transition: reduced
            ? "none"
            : [ease("font-size", shift, EASE_MOVE), ease("letter-spacing", shift, EASE_MOVE)].join(
                ", ",
              ),
        }}
      >
        {displayTitle}
      </h1>
      <div
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: compact ? "clamp(14px, 1.3vw, 17px)" : "clamp(16px, 1.6vw, 22px)",
          fontWeight: 400,
          color: TEXT_DIM,
          letterSpacing: "0.005em",
          marginTop: 18,
          transition: reduced ? "none" : ease("font-size", shift, EASE_MOVE),
        }}
      >
        {composer ?? ", "}
        {tonality !== null && (
          <span
            // Elle se pose ici pendant qu'elle s'éteint là-bas, et les deux
            // temps se recouvrent : l'oeil voit une donnée changer de place, et
            // non deux évènements. C'est le fil qui coud les deux états.
            style={{
              opacity: compact ? 1 : 0,
              transition: reduced ? "none" : ease("opacity", ARRIVE.key, EASE_OUT),
            }}
          >
            {" · "}
            {tonality}
          </span>
        )}
      </div>

      {context !== null && context !== "" && (
        <Collapsible
          gone={compact}
          shift={shift}
          fade={pick(FADE.context, compact)}
          pad={12}
        >
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "clamp(12px, 1.1vw, 15px)",
              fontStyle: "italic",
              color: TEXT_MUTE,
              letterSpacing: "0.01em",
              maxWidth: 720,
            }}
          >
            {context}
          </div>
        </Collapsible>
      )}
    </div>
  );
}

/** Le `clamp` d'une taille, réduit du facteur de l'état de choix. */
function clampPx(scale: TitleScale, ratio: number): string {
  const round = (value: number) => Math.round(value * 10) / 10;

  return `clamp(${round(scale.min * ratio)}px, ${round(scale.vw * ratio)}vw, ${round(
    scale.max * ratio,
  )}px)`;
}

function prettifyTitle(raw: string): string {
  if (!raw) {
    return raw;
  }

  const trimmed = raw.trim();

  if (trimmed.length === 0) {
    return trimmed;
  }

  const isAllLower = trimmed === trimmed.toLowerCase();
  const isAllUpper = trimmed === trimmed.toUpperCase();

  if (isAllLower || isAllUpper) {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  }

  return trimmed;
}

function pickScale(title: string): TitleScale {
  const len = title.length;

  if (len <= 18) {
    return { min: 56, vw: 7.5, max: 96, tracking: -0.045, lineHeight: 1.05 };
  }

  if (len <= 30) {
    return { min: 44, vw: 5.5, max: 72, tracking: -0.035, lineHeight: 1.1 };
  }

  if (len <= 45) {
    return { min: 34, vw: 4, max: 54, tracking: -0.025, lineHeight: 1.15 };
  }

  return { min: 26, vw: 3, max: 40, tracking: -0.015, lineHeight: 1.2 };
}
